import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";
import { normalizeLocalDate } from "@/lib/date-utils";

export type Comissao = {
  id: string;
  user_id: string;
  dentista_id: string;
  referencia: string;
  valor_total_procedimentos: number;
  percentual_comissao: number;
  valor_comissao: number;
  data_inicio: string;
  data_fim: string;
  status: "Pendente" | "Paga" | "Cancelada";
  data_pagamento?: string;
  forma_pagamento?: string;
  observacoes?: string;
  created_at: string;
  updated_at: string;
};

const normalizeComissao = <T extends Partial<Comissao>>(data: T): T => ({
  ...data,
  ...(data.data_inicio ? { data_inicio: normalizeLocalDate(data.data_inicio) } : {}),
  ...(data.data_fim ? { data_fim: normalizeLocalDate(data.data_fim) } : {}),
  ...("data_pagamento" in data ? { data_pagamento: normalizeLocalDate(data.data_pagamento) } : {}),
});

export function useComissoes() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const activeClinicaId = useActiveClinicaId();

  const { data: comissoes = [], isLoading } = useQuery({
    queryKey: ["comissoes", activeClinicaId],
    queryFn: async () => {
      const data = await apiList<Comissao>("comissoes");
      return data.map(normalizeComissao).sort((a, b) => String(b.data_inicio || "").localeCompare(String(a.data_inicio || "")));
    },
    enabled: !!user,
  });

  const createMutation = useMutation({
    mutationFn: async (data: Omit<Comissao, "id" | "user_id" | "created_at" | "updated_at">) => {
      return apiCreate<Comissao>("comissoes", { ...normalizeComissao(data), user_id: user?.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comissoes", activeClinicaId] });
      toast({
        title: "Sucesso",
        description: "Comissão cadastrada com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: "Erro ao cadastrar comissão: " + error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: Partial<Comissao> & { id: string }) => {
      return apiUpdate<Comissao>("comissoes", id, normalizeComissao(data));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comissoes", activeClinicaId] });
      toast({
        title: "Sucesso",
        description: "Comissão atualizada com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: "Erro ao atualizar comissão: " + error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiDelete("comissoes", id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comissoes", activeClinicaId] });
      toast({
        title: "Sucesso",
        description: "Comissão excluída com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro",
        description: "Erro ao excluir comissão: " + error.message,
        variant: "destructive",
      });
    },
  });

  return {
    comissoes,
    isLoading,
    createComissao: createMutation.mutateAsync,
    updateComissao: updateMutation.mutateAsync,
    deleteComissao: deleteMutation.mutateAsync,
  };
}
